import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NametagSticker } from '../components/Nametag';
import { Btn, Chip, Kicker, Stat, GlassPanel } from '../components/ui';
import { Terminal } from '../components/Terminal';
import { supabase } from '../lib/supabase';
import { isNetworkError } from '../lib/isNetworkError';
import { projectsFallback, getProjectFromFallback } from '../data/projectsFallback';
import { FallbackBanner } from '../components/FallbackBanner';

interface HomePageProps {
  onOpenChat?: () => void;
}

interface FeaturedRow {
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  tech_stack: string[] | null;
  featured: boolean;
  year_completed: number | null;
}

interface SiteStats {
  projects_live: number;
  chats_30d: number;
  chats_7d: number;
  visitors_7d: number;
  articles_published: number;
}

const SUGGESTIONS = [
  'what is the workaround?',
  'tech stack?',
  'hire joe?',
];

const DASH = '—';

const HomePage: React.FC<HomePageProps> = ({ onOpenChat }) => {
  const [featured, setFeatured] = useState<FeaturedRow[] | null>(null);
  const [featuredError, setFeaturedError] = useState<string | null>(null);
  const [stats, setStats] = useState<SiteStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('slug, name, tagline, description, tech_stack, featured, year_completed')
          .neq('status', 'private')
          .order('featured', { ascending: false })
          .order('year_completed', { ascending: false, nullsFirst: false })
          .limit(3);
        if (cancelled) return;
        if (error) throw new Error(error.message);
        setFeatured((data ?? []) as FeaturedRow[]);
      } catch (e) {
        if (cancelled) return;
        if (isNetworkError(e)) {
          setFeatured(
            projectsFallback
              .filter(p => p.featured)
              .slice(0, 3) as unknown as FeaturedRow[]
          );
          setUsingFallback(true);
          return;
        }
        setFeaturedError(e instanceof Error ? e.message : String(e));
      }
    })();

    (async () => {
      try {
        const { data, error } = await supabase.rpc('get_public_site_stats');
        if (cancelled) return;
        if (error) throw new Error(error.message);
        setStats(data as SiteStats);
      } catch (e) {
        if (cancelled) return;
        if (isNetworkError(e)) {
          setUsingFallback(true);
          return;
        }
        setStatsError(e instanceof Error ? e.message : String(e));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-[20%] -right-[10%] w-[520px] h-[520px] rounded-full opacity-90"
          style={{
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--accent) 55%, transparent) 0%, transparent 65%)',
            filter: 'blur(20px)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-[30%] left-[10%] w-[460px] h-[460px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--accent-alt) 45%, transparent) 0%, transparent 70%)',
            filter: 'blur(24px)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
            maskImage:
              'radial-gradient(ellipse at 30% 40%, black 0%, transparent 70%)',
            WebkitMaskImage:
              'radial-gradient(ellipse at 30% 40%, black 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 max-w-content mx-auto px-s6 md:px-s12 pt-s16 pb-[88px] grid md:grid-cols-[1.2fr_1fr] gap-s12 md:gap-s16 items-start">
          <div>
            <Kicker>charlotte, nc</Kicker>
            <div className="mt-s6 mb-s6">
              <NametagSticker size="md" />
            </div>
            <p className="font-mono text-[18px] md:text-[22px] leading-[1.35] tracking-[-0.5px] max-w-[560px]">
              <span className="text-text">I wonder if something can be built.</span>{' '}
              <span className="text-text-muted">Then I try.</span>{' '}
              <span className="text-accent">Then I share how.</span>
            </p>
            <p className="mt-s4 text-[14px] text-text-muted leading-[1.6] max-w-[540px]">
              This site is one of those tries.
            </p>
            <div className="mt-s8 flex flex-wrap gap-s3">
              <Link to="/work">
                <Btn>see the work</Btn>
              </Link>
              <Btn variant="ghost" onClick={onOpenChat}>
                ask joe ↗
              </Btn>
            </div>
          </div>

          <div className="relative">
            <GlassPanel tinted radius="sm" className="relative p-s6">
              <div
                className="absolute -top-[10px] left-s4 font-mono text-[10px] uppercase tracking-[1.2px] text-accent px-s2"
                style={{ background: 'var(--bg)' }}
              >
                glass &middot; tinted
              </div>
              <div className="flex items-center gap-s2 mb-[14px]">
                <span
                  className="w-2 h-2 rounded-full bg-ok"
                  style={{ boxShadow: '0 0 8px var(--ok)' }}
                />
                <span className="font-mono text-[10px] uppercase tracking-[1px] text-text-muted">
                  ask-joe &middot; online &middot; multi-model
                </span>
              </div>
              <div
                className="p-[14px] rounded-xs text-[14px] leading-[1.55] text-text"
                style={{ background: 'var(--surface)' }}
              >
                Hey. I'm an AI trained on Joe's work, projects, and experience.
                Ask me anything {DASH} what he's built, his technical approach,
                or how he solves complex problems.
              </div>
              <div className="flex flex-wrap gap-1.5 mt-s3">
                {SUGGESTIONS.map(s => (
                  <Chip key={s} onClick={onOpenChat}>
                    {s}
                  </Chip>
                ))}
              </div>
              <button
                type="button"
                onClick={onOpenChat}
                className="mt-s3 w-full flex items-center gap-s2 px-s3 py-2.5 rounded-xs border border-border bg-bg-elev text-left"
              >
                <span className="text-accent font-mono">›</span>
                <span className="text-text-dim text-[13px]">ask something…</span>
                <span className="flex-1" />
                <span className="font-mono text-[10px] text-text-dim border border-border px-1.5 py-0.5 rounded-xs">
                  ⌘ K
                </span>
              </button>
            </GlassPanel>
          </div>
        </div>
      </section>

      {usingFallback && (
        <div className="max-w-content mx-auto px-s6 md:px-s12 pt-s6">
          <FallbackBanner />
        </div>
      )}

      {/* FEATURED WORK */}
      <section className="border-b border-border">
        <div className="max-w-content mx-auto px-s6 md:px-s12 py-s16">
          <div className="flex items-baseline justify-between mb-s8 flex-wrap gap-s3">
            <div>
              <Kicker>featured</Kicker>
              <h2 className="font-mono text-[26px] font-medium mt-2.5 tracking-[-0.4px] text-text">
                three things I'm proud of
              </h2>
            </div>
            <Link
              to="/work"
              className="font-mono text-[11px] text-text-muted hover:text-accent uppercase tracking-[0.6px]"
            >
              all work →
            </Link>
          </div>

          {featuredError && (
            <div className="font-mono text-[11px] text-err">// supabase: {featuredError}</div>
          )}
          {!featuredError && featured === null && (
            <div className="grid md:grid-cols-3 gap-s5">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-[180px] bg-surface border border-border rounded-xs animate-pulse" />
              ))}
            </div>
          )}
          {!featuredError && featured && featured.length === 0 && (
            <div className="font-mono text-[11px] text-text-dim">
              // no projects loaded &middot; check supabase connection
            </div>
          )}
          {featured && featured.length > 0 && (
            <div className="grid md:grid-cols-3 gap-s5">
              {featured.map((p, i) => (
                <Link
                  key={p.slug}
                  to={`/work/${p.slug}`}
                  className="relative p-s6 bg-bg-elev border border-border rounded-xs overflow-hidden hover:border-border-hi transition-colors"
                >
                  <div className="font-mono text-[11px] text-text-dim uppercase tracking-[1.2px]">
                    project {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="font-sans text-[22px] font-semibold mt-1.5 mb-2.5 tracking-[-0.4px] text-text">
                    {p.name}
                  </div>
                  <div className="text-[13px] text-text-muted leading-[1.55] mb-s3">
                    {p.tagline || (p.description ? p.description.slice(0, 120) : '')}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(p.tech_stack ?? []).slice(0, 4).map(tag => (
                      <Chip key={tag}>{tag}</Chip>
                    ))}
                  </div>
                  <div className="absolute top-s4 right-s4 w-7 h-7 rounded-full flex items-center justify-center text-accent text-[12px] border border-border">
                    ↗
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* RECEIPTS */}
      <section className="border-b border-border">
        <div className="max-w-content mx-auto px-s6 md:px-s12 py-[56px]">
          {usingFallback ? (
            <>
              <Kicker muted>shipped &middot; not promised</Kicker>
              <h2 className="font-mono text-[22px] font-medium mt-2.5 mb-s8 tracking-[-0.4px] text-text">
                what I've built, bundled.
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-s4">
                <Stat
                  value={projectsFallback.filter(p => p.status === 'active').length}
                  label="projects · shipped"
                />
                <Stat value="25" unit="+" label="ai models wired up" />
                <Stat
                  value={String(getProjectFromFallback('servet-digital')?.metrics?.professionals_trained ?? '—')}
                  label="federal pros · trained"
                />
                <Stat value="2" label="sdvosb · wosb certs" />
              </div>
              <div className="mt-s5 font-mono text-[10px] text-text-dim tracking-[0.4px]">
                // bundled with the site &middot; works offline on purpose.
              </div>
            </>
          ) : (
            <>
              <Kicker muted>receipts &middot; not vibes</Kicker>
              <h2 className="font-mono text-[22px] font-medium mt-2.5 mb-s8 tracking-[-0.4px] text-text">
                what this site can prove, live.
              </h2>

              {statsError ? (
                <div className="font-mono text-[11px] text-err">// supabase: {statsError}</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-s4">
                  <Stat value={stats ? stats.projects_live : '—'} label="projects · live" />
                  {/* TODO: model count config in admin */}
                  <Stat value="25" unit="+" label="ai models wired up" />
                  <Stat value={stats ? stats.chats_30d : '—'} label="conversations · 30d" />
                  <Stat value={stats ? stats.visitors_7d : '—'} label="visitors · 7d" />
                </div>
              )}
              <div className="mt-s5 font-mono text-[10px] text-text-dim tracking-[0.4px]">
                // pulled from git + live services, not a dashboard someone optimizes.
              </div>
            </>
          )}
        </div>
      </section>

      {/* TERMINAL CTA */}
      <section>
        <div className="max-w-content mx-auto px-s6 md:px-s12 py-[56px] grid md:grid-cols-[1fr_1.1fr] gap-s10 items-center">
          <div>
            <Kicker>or, if you prefer</Kicker>
            <h2 className="font-mono text-[26px] font-medium mt-2.5 mb-s3 tracking-[-0.4px] text-text">
              poke around in a shell.
            </h2>
            <p className="text-[14px] text-text-muted leading-[1.6] max-w-[440px]">
              Same content, fewer pixels. Try{' '}
              <code className="font-mono bg-surface px-1.5 py-0.5 rounded-xs text-accent">help</code>{' '}
              to start.
            </p>
          </div>
          <div className="h-[280px]">
            <Terminal />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
